import { useCallback, useEffect, useRef, useState } from 'react';

export const DEFAULT_AUTO_REFRESH_INTERVAL_MS = 15_000;

export interface UseGridAutoRefreshOptions {
  intervalMs?: number;
  forceOn?: boolean;
}

export interface UseGridAutoRefreshResult {
  autoRefresh: boolean;
  toggleAutoRefresh: () => void;
}

/**
 * Drives opt-in auto-refresh for a grid. When `autoRefresh` is on AND the browser
 * tab is visible, calls `onRefresh` every `intervalMs`. Polling is paused while the
 * tab is hidden (mirrors the visibility pattern in BackgroundServicePodsView) and
 * resumes when the tab becomes visible again.
 *
 * `onRefresh` is held in a ref so the interval is not torn down on every render —
 * only autoRefresh/visibility/intervalMs changes restart the timer. The interval is
 * always cleared on unmount.
 *
 * The data model is the caller's concern: pass `refetch()` for client-side RTK Query
 * grids or `() => api.refreshServerSide({ purge: true })` for server-side row model grids.
 */
export const useGridAutoRefresh = (
  onRefresh: () => void,
  options: UseGridAutoRefreshOptions = {},
): UseGridAutoRefreshResult => {
  const { intervalMs = DEFAULT_AUTO_REFRESH_INTERVAL_MS, forceOn = false } = options;

  const [autoRefresh, setAutoRefresh] = useState(false);
  const polling = autoRefresh || forceOn;
  const [isPageVisible, setIsPageVisible] = useState(
    typeof document !== 'undefined' ? !document.hidden : true,
  );

  // Keep the latest callback in a ref so the polling interval is not recreated on
  // every render when the consumer passes a fresh function each time.
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const toggleAutoRefresh = useCallback(() => {
    if (forceOn) return;
    setAutoRefresh((prev) => !prev);
  }, [forceOn]);

  // Track tab visibility so polling pauses while the tab is hidden.
  useEffect(() => {
    const handleVisibilityChange = () => setIsPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Run the interval only while auto-refresh is on and the tab is visible.
  useEffect(() => {
    if (!polling || !isPageVisible) return;

    const id = setInterval(() => onRefreshRef.current(), intervalMs);
    return () => clearInterval(id);
  }, [polling, isPageVisible, intervalMs]);

  return { autoRefresh: polling, toggleAutoRefresh };
};

export default useGridAutoRefresh;
